import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import toast from 'react-hot-toast';
import client from '../../../lib/feathers';
import Breadcrumb from '../../../components/Breadcrumb';
import Page from '../../../components/Page';

/**
 * Wineries reported missing by users.
 *
 * Each row shows the claim next to what the crawler made of it, because those
 * two together are the decision: a report whose site crawls to a real winery
 * with a matching name is obvious, and one where the crawl found nothing is
 * equally obvious the other way.
 *
 * Rejecting destroys nothing — check-ins made there keep the name of the place
 * the reporter thought they were at, and wines logged there survive without a
 * producer. The confirm text says so, because an admin shouldn't have to guess
 * what a button does to other people's data.
 */
const STATUSES = [
  { key: 'pending', label: 'Needs review' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' },
];

function timeAgo(iso) {
  if (!iso) return '—';
  try {
    return `${formatDistanceToNow(new Date(iso))} ago`;
  } catch (e) {
    return '—';
  }
}

function EnrichmentBadge({ enrichment }) {
  if (!enrichment) {
    return <span className="text-xs text-gray-400">not crawled yet</span>;
  }
  const ok = enrichment.outcome === 'ok' || enrichment.outcome === 'partial';
  const cls = ok ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-800';
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${cls}`} title={enrichment.error || ''}>
      crawl: {enrichment.outcome}
    </span>
  );
}

export default function ListingSubmissions() {
  const [status, setStatus] = useState('pending');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await client.service('admin/listing-submission').find({
        query: { status, $limit: 100 },
      });
      setRows(res.data || []);
    } catch (e) {
      toast.error(e.message || 'Could not load submissions');
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const decide = async (row, decision) => {
    let reason = null;
    if (decision === 'rejected') {
      // eslint-disable-next-line no-alert
      reason = window.prompt(
        `Reject "${row.rawName}"?\n\nNothing anyone made gets deleted: check-ins there keep the name as the place they thought they were, and any wines logged there stay, minus the producer link.\n\nReason (optional):`,
        '',
      );
      if (reason === null) return;
    }

    setBusyId(row.id);
    try {
      const result = await client.service('admin/listing-submission').patch(row.id, {
        status: decision,
        reason: reason || undefined,
      });
      if (decision === 'approved') {
        toast.success(result.claimRequested
          ? 'Approved — and their claim is now in the listing\'s Users tab'
          : 'Approved');
      } else {
        const moved = result.moved || {};
        toast.success(`Rejected — kept ${moved.checkins || 0} check-in(s) and ${moved.libations || 0} wine(s)`);
      }
      await load();
    } catch (e) {
      toast.error(e.message || 'Could not save that decision');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Page>
      <Breadcrumb pages={[
        { name: 'Reports', href: '/admin/reports', current: false },
        { name: 'Reported Wineries', href: '#', current: true },
      ]}
      />

      <div className="px-4 sm:px-6 lg:px-8">
        <div className="sm:flex sm:items-center">
          <div className="sm:flex-auto">
            <h1 className="text-xl font-semibold text-gray-900">Reported Wineries</h1>
            <p className="mt-2 text-sm text-gray-700">
              Wineries users told us were missing. They&apos;re already crawled and usable
              by the person who reported them, but stay out of search and the map
              until you approve them.
            </p>
          </div>
        </div>

        <div className="mt-4 flex gap-2">
          {STATUSES.map((s) => (
            <button
              key={s.key}
              type="button"
              onClick={() => setStatus(s.key)}
              className={`rounded-full px-3 py-1 text-sm ${status === s.key ? 'bg-red-800 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            >
              {s.label}
            </button>
          ))}
        </div>

        {loading && <p className="mt-6 text-sm text-gray-500">Loading…</p>}

        {!loading && rows.length === 0 && (
          <p className="mt-6 text-sm text-gray-500">
            {status === 'pending' ? 'Nothing waiting. ' : 'Nothing here. '}
            {status === 'pending' && 'Reports show up as users find gaps in the catalog.'}
          </p>
        )}

        <div className="mt-6 space-y-4">
          {rows.map((row) => (
            <div key={row.id} className="rounded-lg bg-white shadow">
              <div className="flex flex-wrap items-start justify-between gap-4 border-b border-gray-200 px-4 py-4 sm:px-6">
                <div className="min-w-0">
                  <h3 className="text-lg font-medium text-gray-900">
                    {row.listing
                      ? <Link className="hover:underline" to={`/admin/listings/${row.listingId}`}>{row.listing.name}</Link>
                      : row.rawName}
                  </h3>
                  <p className="mt-1 text-xs text-gray-500">
                    Reported {timeAgo(row.createdAt)} by{' '}
                    {row.submittedBy ? (
                      <Link className="hover:underline" to={`/admin/users/${row.submittedBy.id}`}>
                        {[row.submittedBy.firstName, row.submittedBy.lastName].filter(Boolean).join(' ') || row.submittedBy.email}
                      </Link>
                    ) : 'a deleted account'}
                    {row.submittedBy && ` · member ${timeAgo(row.submittedBy.createdAt)}`}
                  </p>
                </div>

                {status === 'pending' && (
                  <div className="flex flex-none gap-2">
                    <button
                      type="button"
                      disabled={busyId === row.id}
                      onClick={() => decide(row, 'approved')}
                      className="rounded-md bg-green-700 px-3 py-2 text-sm font-medium text-white hover:bg-green-800 disabled:opacity-50"
                    >
                      Approve
                    </button>
                    <button
                      type="button"
                      disabled={busyId === row.id}
                      onClick={() => decide(row, 'rejected')}
                      className="rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Reject
                    </button>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 gap-4 px-4 py-4 text-sm sm:grid-cols-2 sm:px-6">
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-400">They told us</p>
                  <dl className="mt-1 space-y-1 text-gray-700">
                    <div><dt className="inline text-gray-500">Name: </dt><dd className="inline">{row.rawName}</dd></div>
                    {row.rawWebsite && (
                      <div>
                        <dt className="inline text-gray-500">Site: </dt>
                        <dd className="inline">
                          <a href={row.rawWebsite} target="_blank" rel="noreferrer" className="text-red-800 hover:underline">
                            {row.rawWebsite}
                          </a>
                        </dd>
                      </div>
                    )}
                    {(row.rawCity || row.rawState) && (
                      <div>
                        <dt className="inline text-gray-500">Where: </dt>
                        <dd className="inline">{[row.rawCity, row.rawState].filter(Boolean).join(', ')}</dd>
                      </div>
                    )}
                    {row.submittedLat != null && (
                      <div>
                        <dt className="inline text-gray-500">Filed from: </dt>
                        <dd className="inline">
                          <a
                            className="text-red-800 hover:underline"
                            href={`https://www.google.com/maps?q=${row.submittedLat},${row.submittedLng}`}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {row.submittedLat.toFixed(4)}, {row.submittedLng.toFixed(4)}
                          </a>
                        </dd>
                      </div>
                    )}
                    {row.note && <div className="text-gray-600 italic">“{row.note}”</div>}
                    {row.worksHere && (
                      <div className="mt-1">
                        <span className="inline-flex rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
                          says they work here
                        </span>
                      </div>
                    )}
                  </dl>
                </div>

                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-400">What we found</p>
                  <div className="mt-1 space-y-1 text-gray-700">
                    <div><EnrichmentBadge enrichment={row.enrichment} /></div>
                    {row.listing && row.listing.address && (row.listing.address.city || row.listing.address.address1) && (
                      <p>{[row.listing.address.address1, row.listing.address.city, row.listing.address.state].filter(Boolean).join(', ')}</p>
                    )}
                    {row.listing && row.listing.contact && row.listing.contact.website && (
                      <p>
                        <a href={row.listing.contact.website} target="_blank" rel="noreferrer" className="text-red-800 hover:underline">
                          {row.listing.contact.website}
                        </a>
                      </p>
                    )}
                    {row.listing && row.listing.shortDescription && (
                      <p className="text-gray-600 line-clamp-3">{row.listing.shortDescription}</p>
                    )}
                    {row.listing && !row.listing.shortDescription && !row.enrichment && (
                      <p className="text-gray-400">Nothing yet — the crawler hasn&apos;t reached it.</p>
                    )}
                  </div>
                </div>
              </div>

              {row.status !== 'pending' && (
                <div className="border-t border-gray-200 px-4 py-2 text-xs text-gray-500 sm:px-6">
                  {row.status === 'approved' ? 'Approved' : 'Rejected'} {timeAgo(row.decidedAt)}
                  {row.decidedBy && ` by ${[row.decidedBy.firstName, row.decidedBy.lastName].filter(Boolean).join(' ') || row.decidedBy.email}`}
                  {row.decisionReason && ` — ${row.decisionReason}`}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </Page>
  );
}
